// src/encounter/services/note-session.ts
//
// PROD-3107 (Andrew, 7 Oct 2026). Offline, React Query PAUSES a note save instead of failing it, and resumes it on
// reconnect even after the encounter page is gone. So a save that could not go out, followed by "Discard" in the leave
// guard, still wrote the discarded text when the browser came back online.
//
// Every visit's mount opens a NOTE SESSION (encounter-route.tsx). Each note write is bound to the session it was started
// in and checks it when it actually RUNS: if that session's note was discarded, the write is refused without reaching
// the server, and the mutation settles as an error (it does not hang). Scoped to one visit: another encounter's saves,
// or the next page's, are never touched (Codex P1 on the first version, which removed every paused note save).
import { createContext, useCallback, useContext } from 'react';

let seq = 0;

/** One visit's note session. An object, so it lives only as long as the mounts and the paused writes that hold it
 *  (Codex P3: a global list of discarded ids would grow for the life of the app). */
export interface NoteSession {
  readonly id: string;
  discarded: boolean;
}

/** A new session for one mount of one appointment's encounter. */
export function newNoteSession(appointmentId: string | undefined): NoteSession {
  seq += 1;
  return { id: `${appointmentId ?? 'no-appointment'}#${seq}`, discarded: false };
}

/** The session of the encounter being rendered; null outside an encounter (a write is then never refused). */
export const NoteSessionContext = createContext<NoteSession | null>(null);

/** The clinician chose Discard: no write of this session reaches the server from now on, even one already paused. */
export function discardNoteSession(session: NoteSession | null): void {
  if (session) session.discarded = true;
}

export function isNoteSessionDiscarded(session: NoteSession | null): boolean {
  return session?.discarded === true;
}

export class NoteDiscardedError extends Error {
  constructor() {
    super('The note was discarded; this save was not sent.');
    this.name = 'NoteDiscardedError';
  }
}

/** Wraps a note write so it is checked against its visit's session at the moment it runs. */
export function useNoteWrite() {
  const session = useContext(NoteSessionContext);
  return useCallback(
    <T,>(write: () => Promise<T>): Promise<T> => (isNoteSessionDiscarded(session) ? Promise.reject(new NoteDiscardedError()) : write()),
    [session],
  );
}
